import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useNavigate } from 'react-router-dom';
import { AutoComplete, message, Input } from 'antd';
import { MAP_API_KEY } from '../../../utils/constants';
import { isEmpty } from '../../../utils/helpers';
import {
  get, isSuperAgentError, RequestWrapper, ResponseWrapper,
} from '../../../dataSource/requests';
import './AutocompleteGoogleMaps.scss';
import { isLatitude, isLongitude } from '../../../utils/LocationUtils';

const AutocompleteGoogleMaps = (props): JSX.Element => {
  const navigate = useNavigate();
  const [placeOptions, setPlaceOptions] = useState([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    return () => {
      setLoading(false);
    };
  }, []);

  const handlePlaceSearch = _.debounce(async (value) => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const { handleMarkerPosition, handleSelectedLocation } = props;

    if (value.length < 3) {
      setPlaceOptions([]);
    } else {
      const subValue = value.substr(0, 3);

      if (!Number.isNaN(+subValue)) {
        setPlaceOptions([]);

        const geolocation = value.trim().split(',');

        if (geolocation.length < 2
          || isEmpty(geolocation[0])
          || isEmpty(geolocation[1])
          || Number.isNaN(geolocation[0])
          || Number.isNaN(geolocation[1])
          || !isLatitude(Number.parseFloat(geolocation[0]))
          || !isLongitude(Number.parseFloat(geolocation[1]))
        ) {
          message.error('Insira as coordenadas de latitude e longitude corretamente');
        } else {
          const latlng = { lat: geolocation[0], lng: geolocation[1] };

          handleMarkerPosition({ latlng });
          handleSelectedLocation(geolocation[0], geolocation[1]);
        }
      } else {
        const url = '/land/googleMapsApis/queryAutoComplete';
        const params = { key: MAP_API_KEY, input: value };

        setLoading(true);

        const requestReference: RequestWrapper = get({
          url,
          query: params,

          frontConfigs: {
            navigateFunction: navigate,
          },
        });

        try {
          response = await requestReference;
        } catch (error) {
          if (isSuperAgentError(error)) {
            responseError = error.response;
          } else {
            genericError = error as Error;
          }
        }

        setLoading(false);

        switch (true) {
          case (response?.body?.code === 6008): {
            // "Query auto complete retrieved successfully", 6008
            const jsonResponse = JSON.parse(response?.body?.response);

            if (jsonResponse.status === 'OK') {
              const { predictions } = jsonResponse;

              const newPlaceOptions = [];

              predictions.forEach((place) => {
                if (place.place_id || place.id) {
                  newPlaceOptions.push({
                    label: place.description,
                    value: place.description,
                    place_id: place.place_id || place.id,
                  });
                }
              });

              setPlaceOptions(newPlaceOptions);
            } else {
              message.error(
                'Local não encontrado, tente novamente',
              );
            }
          } break;

          case (responseError?.body?.code === -4001):
            // "You must be authenticated to perform this operation", -4001
            break;

          case (responseError?.body?.code === -4003):
            // "You do not have permission to perform this operation", -4003
            break;

          case (genericError?.message?.includes('has been aborted')):
            // dont show anything when request is aborted
            break;

          default:
            message.error('Ocorreu um erro ao buscar as localizações, tente novamente');
            break;
        }
      }
    }
  }, 600);

  const onSelectPlace = async (selectedValue, optionInstance): Promise<void> => {
    const { handleSelectedLocation } = props;

    const url = '/land/googleMapsApis/details';
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const params = { key: MAP_API_KEY, placeid: optionInstance.place_id };

    setLoading(true);

    const requestReference = get({
      url,
      query: params,

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    setLoading(false);

    switch (true) {
      case (response?.body?.code === 6009): {
        // "Place details retrieved successfully", 6009
        const jsonResponse = JSON.parse(response?.body?.response);

        if (jsonResponse.status === 'OK') {
          const { lat, lng } = jsonResponse.result.geometry.location;

          handleSelectedLocation(lat, lng);
        } else {
          message.error(
            'Ocorreu um erro ao buscar as localizações, tente novamente',
          );
        }
      } break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        message.error(
          'Você não possui permissão para executar esta ação.',
        );
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        message.error('Ocorreu um erro ao buscar as localizações, tente novamente');
        break;
    }
  };

  return (
    <div className="AutocompleteGoogleMaps">
      <AutoComplete
        style={{ width: '100%' }}
        options={placeOptions}
        onSearch={handlePlaceSearch}
        onSelect={onSelectPlace}
      >
        <Input.Search
          placeholder="Buscar cidade, bairro ou coordenada"
          onSearch={
            (text: string): void => {
              handlePlaceSearch(text);
            }
          }
          loading={loading}
        />
      </AutoComplete>
    </div>
  );
};

AutocompleteGoogleMaps.propTypes = {
  handleSelectedLocation: PropTypes.func.isRequired,
  handleMarkerPosition: PropTypes.func.isRequired,
};

AutocompleteGoogleMaps.defaultProps = {};

export default AutocompleteGoogleMaps;
